import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DeviceActionMenu from '@totem/components/devices/DeviceActionMenu';
import DeviceContext from '@totem/components/devices/devicesContainer/deviceContainerContext';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { Device, DeviceQueryResult } from '@totem/types/devices';

import '../../../../devices/devices.css';

const GatewayDeviceOrgTable = () => {
  const { loading, deviceData, totalRecords } = useContext(DeviceContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);

  const getBuildingDisplay = (device: DeviceQueryResult) => {
    if (
      device.building !== null &&
      device.building.id !== null &&
      device.building.id !== EMPTY_ID
    ) {
      return (
        <Link to={`/dashboard/buildings/${device.building.id}`}>
          {device.building.name}
        </Link>
      );
    } else if (device.building !== null) {
      return <span>{device.building.name}</span>;
    }
    return <span>-</span>;
  };

  const getContractLine = (device: Device) => {
    if (device.salesOrders !== null && device.salesOrders.length > 0) {
      const activeLines = device.salesOrders.filter(
        (cl) => cl.status === 'Active',
      );
      return (
        <span>
          {activeLines.map((cl) => {
            if (
              cl.service === 'Remote Access' ||
              cl.service === 'Asset Inventory'
            ) {
              return (
                <Link
                  key={cl.service + cl.salesOrderId + cl.lineNumber}
                  to={`/dashboard/salesOrder/${encodeURIComponent(cl.salesOrderNumber)}`}
                  title={cl.service}
                >
                  {cl.salesOrderNumber}:{cl.lineNumber}
                </Link>
              );
            }
            return (
              <span
                key={cl.service + cl.salesOrderId + cl.lineNumber}
                style={{ color: '#D39600' }}
                title={cl.service}
              >
                {cl.salesOrderNumber}:{cl.lineNumber}
              </span>
            );
          })}
        </span>
      );
    }
    return <span>-</span>;
  };

  const getAttributeValue = (
    device: DeviceQueryResult,
    attributeName: string,
  ) => {
    if (
      device.device !== null &&
      device.device.attributes !== null &&
      device.device.attributes.length > 0
    ) {
      for (let idx = 0; idx < device.device.attributes.length; idx++) {
        if (device.device.attributes[idx].name === attributeName) {
          return <span>{device.device.attributes[idx].value}</span>;
        }
      }
    }
    return <span>-</span>;
  };

  const columns: ColumnProps<DeviceQueryResult>[] = [
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization.name',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <span>{device.organization.name}</span>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.organization.name === compB.organization.name
          ? 0
          : compA.organization.name > compB.organization.name
            ? 1
            : -1,
    },
    {
      title: 'Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <Link to={`/dashboard/devices/${device.device.id}`}>
          {device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.displayName === compB.device.displayName
          ? 0
          : compA.device.displayName > compB.device.displayName
            ? 1
            : -1,
    },
    {
      title: 'Host Name',
      dataIndex: 'device.attributes.host',
      key: 'host',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Hostname'),
    },
    {
      title: 'Model',
      dataIndex: 'device.attributes.model',
      key: 'model',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Model'),
      filterMultiple: true,
      filteredValue: getFilterValues('model'),
      filters: getFilterOptions('model'),
    },
    {
      title: 'Version',
      dataIndex: 'device.attributes.version',
      key: 'version',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Version'),
    },
    {
      title: 'Serial Number',
      dataIndex: 'device.attributes.serialNumber',
      key: 'serialNumber',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Serial Number'),
    },
    {
      title: 'Asset Tag',
      dataIndex: 'device.attributes.assetTag',
      key: 'assetTag',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Asset Tag'),
    },
    {
      title: 'Firmware Revision',
      dataIndex: 'device.attributes.firmware',
      key: 'firmware',
      render: (_, device: DeviceQueryResult) => (
        <span>{device.device.firmware}</span>
      ),
    },
    {
      title: 'Contract Line',
      dataIndex: 'device.contractLine.orderNumber',
      key: 'backupStatus',
      render: (_, device: DeviceQueryResult) => {
        return getContractLine(device.device);
      },
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getBuildingDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.building.name === compB.building.name
          ? 0
          : compA.building.name > compB.building.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'region',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.region.name,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.region.name === compB.region.name
          ? 0
          : compA.region.name > compB.region.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('region'),
      filters: getFilterOptions('region'),
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, device: DeviceQueryResult) => (
        <div className="center-table-cell">
          <DeviceActionMenu device={device} />
        </div>
      ),
    },
  ];

  const getDevices = () => {
    if (deviceData !== null) {
      if (deviceData.devices !== null) {
        return deviceData.devices;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getDevices()}
      loading={loading}
      onChange={(updatedPagination, filters, sorter, extra) =>
        handleTableChange(updatedPagination, filters, sorter, extra)
      }
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.device.id}
    />
  );
};

export default GatewayDeviceOrgTable;
